// @ts-nocheck
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { DateTime } from "luxon";
import AuthService from "src/components/AuthService";
import { sterilisCustomers } from "src/components/library/helpers";
import { alphabeticalSort } from "src/components/library/helpers";
import { isGroupSterilisOrDist } from "src/components/library/helpers";

const auth = new AuthService();

const initialState = {
    loading: {
        devices: false,
        deviceCycles: true,
        customerCycles: true,
        cycleSummary: true,
        cyclesByOperator: true,
        wasteProcessed: true,
        cycleReport: false,
        sterilizationInfo: true,
        biochallengeInfo: true,
        cycleEvents: true,
        customers: true,
        facilities: true,
    },
    userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    toDate: DateTime.now().toISODate(),
    fromDate: DateTime.now().minus({ month: 1 }).toISODate(),
    selectedCycle: null,
    selectedDevice: null,
    cycleMetadata: null,
    customer: null,
    cycleEvents: [],
    deviceTimezone: null,
    cycles: [],
    deviceConfigIds: [],
    deviceSerial: "",
    complianceReport: false,
    comprehensiveReport: false,
    isError: false,
    currentPressureUnit: "PSI(a)",
    facilities: [],
    deviceFacility: "",
    deviceFacilityAddress: "",
    customerName: "",
    facilityType: null,
    customerDevices: [],
    filteredCustomerDevices: [],
    rawCustomerDevices: [],
    biochallengeInfo: [],
    customerDevicesOnly: true,
    weightUnit: "",
    refreshInterval: 30,
    noCyclesFound: false,
    customers: [],
    customerDropdown: [],
    selectedCustomer: null,
    selectedFacilities: [],
};

const summaryDashboardSlice = createSlice({
    name: "summarydashboard",
    initialState,
    reducers: {
        setUserTimezone(state, action) {
            state.userTimezone = action.payload;
        },
        setToDate(state, action) {
            state.toDate = action.payload;
        },
        setFromDate(state, action) {
            state.fromDate = action.payload;
        },
        setSelectedCycle(state, action) {
            state.selectedCycle = action.payload;
        },
        setSelectedDevice(state, action) {
            state.selectedDevice = action.payload;
        },
        setCycleMetadata(state, action) {
            state.cycleMetadata = action.payload;
        },
        setCycleEvents(state, action) {
            state.cycleEvents = action.payload;
        },
        setCycles(state, action) {
            state.cycles = action.payload;
        },
        setDeviceConfigIds(state, action) {
            state.deviceConfigIds = action.payload;
        },
        setDeviceSerial(state, action) {
            state.deviceSerial = action.payload;
        },
        setDeviceTimezone(state, action) {
            state.deviceTimezone = action.payload;
        },
        setComplianceReport(state, action) {
            state.complianceReport = action.payload;
        },
        setComprehensiveReport(state, action) {
            state.comprehensiveReport = action.payload;
        },
        setIsError(state, action) {
            state.isError = action.payload;
        },
        setCurrentPressureUnit(state, action) {
            state.currentPressureUnit = action.payload;
        },
        setCustomerDevices(state, action) {
            state.customerDevices = action.payload;
        },
        setFilteredCustomerDevices(state, action) {
            state.filteredCustomerDevices = action.payload;
        },
        setRawCustomerDevices(state, action) {
            state.rawCustomerDevices = action.payload;
        },
        setBiochallengeInfo(state, action) {
            state.biochallengeInfo = action.payload;
        },
        setCustomerDevicesOnly(state, action) {
            state.customerDevicesOnly = action.payload;
        },
        setWeightUnit(state, action) {
            state.weightUnit = action.payload;
        },
        setRefreshInterval(state, action) {
            state.refreshInterval = action.payload;
        },
        setNoCyclesFound(state, action) {
            state.noCyclesFound = action.payload;
        },
        setFacilities(state, action) {
            state.facilities = action.payload;
        },
        setSelectedFacilities(state, action) {
            state.selectedFacilities = action.payload;
        },
        setSelectedCustomer(state, action) {
            state.selectedCustomer = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCustomerInfo.pending, (state) => {
                state.loading.customerInfo = true;
                state.loading.customerCycles = true;
                state.loading.cycleSummary = true;
                state.loading.cyclesByOperator = true;
                state.loading.wasteProcessed = true;

                state.customer = initialState.customer;
                state.customerName = initialState.customerName;
                state.facilities = initialState.facilities;
                state.cycles = initialState.cycles;
                state.selectedCycle = initialState.selectedCycle;
                state.deviceConfigIds = initialState.deviceConfigIds;
            })
            .addCase(fetchCustomerInfo.fulfilled, (state, action) => {
                state.customer = action.payload;
                state.customerName = action.payload["customer_name"];
                state.facilities = action.payload["facilities"];
                state.weightUnit = action.payload["weight_unit_preference"];
                state.deviceConfigIds = action.payload["facilities"].reduce((acc, facility) => {
                    const deviceConfigIds = facility["device_configs"].filter((deviceConfig) => deviceConfig.deleted === null).map((deviceConfig) => deviceConfig.id);
                    return acc.concat(deviceConfigIds);
                }, []);
                state.loading.customerInfo = false;
            })
            .addCase(fetchCustomerInfo.rejected, (state) => {
                state.loading.customerInfo = false;
                state.isError = true;
            })
            .addCase(fetchCustomerCycles.pending, (state) => {
                state.loading.customerCycles = true;
                state.loading.cycleSummary = true;
                state.loading.cyclesByOperator = true;
                state.loading.wasteProcessed = true;
                state.cycles = initialState.cycles;
                state.selectedCycle = initialState.selectedCycle;
            })
            .addCase(fetchCustomerCycles.fulfilled, (state, action) => {
                state.cycles = action.payload["device_cycles"];
                if (action.payload["cycle_metadata"]["new_from_date"]) {
                    state.fromDate = DateTime.fromISO(action.payload["cycle_metadata"]["new_from_date"]).toISODate();
                }
                state.selectedCycle = state.selectedCycle || sortCyclesByDate(action.payload["device_cycles"])[0];
                state.loading.customerCycles = false;
                state.loading.cycleSummary = false;
                state.loading.cyclesByOperator = false;
                state.loading.wasteProcessed = false;
            })
            .addCase(fetchCustomerCycles.rejected, (state) => {
                state.loading.customerCycles = false;
                state.isError = true;
            })
            .addCase(fetchFacilityCycles.pending, (state) => {
                state.loading.deviceCycles = true;
                state.loading.cycleEvents = true;
                state.cycles = initialState.cycles;
                //state.cycleEvents = initialState.cycleEvents;
                state.loading.cycleSummary = true;
                state.loading.cyclesByOperator = true;
                state.loading.wasteProcessed = true;
            })
            .addCase(fetchFacilityCycles.fulfilled, (state, action) => {
                state.cycles = sortCyclesByDate(action.payload["device_cycles"]);
                state.noCyclesFound = action.payload["device_cycles"].length > 0 ? false : true;
                state.customerName = action.payload["device_metadata"]["customer_name"];
                state.deviceFacility = action.payload["device_metadata"]["device_facility_name"];
                state.facilityType = action.payload["device_metadata"]["facility_type"];
                state.deviceFacilityAddress = action.payload["device_metadata"]["device_facility_full_address"];
                state.deviceSerial = action.payload["device_metadata"]["device_serial"];
                state.weightUnit = action.payload["device_metadata"]["weight_unit"];
                if (action.payload["cycle_metadata"]["new_from_date"]) {
                    state.fromDate = DateTime.fromISO(action.payload["cycle_metadata"]["new_from_date"]).toISODate();
                }
                state.selectedCycle = state.selectedCycle || sortCyclesByDate(action.payload["device_cycles"])[0];

                state.loading.deviceCycles = false;
                state.loading.cycleSummary = false;
                state.loading.cyclesByOperator = false;
                state.loading.wasteProcessed = false;
            })
            .addCase(fetchFacilityCycles.rejected, (state) => {
                state.isError = true;
                state.loading.deviceCycles = false;
                state.loading.cycleSummary = false;
                state.loading.cyclesByOperator = false;
                state.loading.wasteProcessed = false;
            })
            .addCase(fetchCycleEvents.pending, (state, action) => {
                state.cycleEvents = initialState.cycleEvents;
                state.loading.cycleEvents = true;
                state.loading.sterilizationInfo = true;
            })
            .addCase(fetchCycleEvents.fulfilled, (state, action) => {
                state.cycleEvents = action.payload["cycle_events"];
                state.cycleMetadata = action.payload["cycle_metadata"];
                state.loading.cycleEvents = false;
                state.loading.sterilizationInfo = false;
            })
            .addCase(fetchCycleEvents.rejected, (state, action) => {
                state.loading.cycleEvents = false;
                state.loading.sterilizationInfo = false;
                state.isError = true;
            })
            .addCase(fetchCustomerDevices.pending, (state, action) => {
                state.loading.devices = true;
            })
            .addCase(fetchCustomerDevices.fulfilled, (state, action) => {
                state.customer = action.payload;
                state.customerName = action.payload["customer_name"];
                state.facilities = action.payload["facilities"];
                state.weightUnit = action.payload["weight_unit_preference"];
                state.deviceConfigIds = action.payload["facilities"].reduce((acc, facility) => {
                    const deviceConfigIds = facility["device_configs"].filter((deviceConfig) => deviceConfig.deleted === null).map((deviceConfig) => deviceConfig.id);
                    return acc.concat(deviceConfigIds);
                }, []);
                state.loading.customerInfo = false;
            })
            .addCase(fetchCustomerDevices.rejected, (state, action) => {
                state.loading.devices = false;
                state.isError = true;
            })
            .addCase(fetchBiochallengeInfo.pending, (state, action) => {
                state.loading.biochallengeInfo = true;
            })
            .addCase(fetchBiochallengeInfo.fulfilled, (state, action) => {
                state.biochallengeInfo = action.payload;
                state.loading.biochallengeInfo = false;
            })
            .addCase(fetchBiochallengeInfo.rejected, (state) => {
                state.loading.biochallengeInfo = false;
                state.isError = true;
            })
            .addCase(fetchCustomers.pending, (state) => {
                state.loading.customers = true;
                state.customers = initialState.customers;
                state.facilities = initialState.facilities;
                state.customerDropdown = initialState.customerDropdown;
            })
            .addCase(fetchCustomers.fulfilled, (state, action) => {
                state.loading.customers = false;
                state.customers = action.payload;

                action.payload.forEach((customer) => {
                    state.customerDropdown.push({
                        id: customer.id,
                        value: customer.id,
                        name: customer.customer_name,
                        dist_name: customer?.distributor.distributor_name,
                        dist_id: customer?.distributor.id,
                    });

                });
                state.customerDropdown = state.customerDropdown.sort((a, b) => alphabeticalSort(a, b, "name"));
            })
            .addCase(fetchCustomers.rejected, (state) => {
                state.loading.customers = false;
                state.customers = initialState.customers;
                state.facilities = initialState.facilities;
                state.customerDropdown = initialState.customerDropdown;
            })
    },
});

const sortCyclesByDate = (cycles) => {
    return cycles.sort((a, b) => {
        const aVal = new Date(a.time_started);
        const bVal = new Date(b.time_started);

        return aVal < bVal ? 1 : -1;
    });
};

export const selectDeviceById = (state, id) => state.customerDevices.find((device) => device.id === id);

export const isFacilityAirport = (state) => {
    return !state?.facilityType || state?.facilityType === "airport";
};

export const fetchCustomerInfo = createAsyncThunk("summarydashboard/fetchCustomerInfo", async (customerId) => {
    const response = await auth.fetch(`/api/customer-facilities/${customerId}`);
    return response;
});

export const fetchCustomerDevices = createAsyncThunk("summarydashboard/fetchCustomerDevices", async (customerId) => {
    const response = await auth.fetch(`/api/customer-machines/${customerId}`);
    return response;
});

export const fetchFacilityCycles = createAsyncThunk("summarydashboard/fetchFacilityCycles", async (arg, { getState }) => {
    const state = getState();
    const facilityCycleParams = new URLSearchParams({
        facility_id: state.summarydashboard.facility_id,
        customer_id: state.summarydashboard.customer_id,
        // Add/Subtract 1 day so that the date range is inclusive
        from_date: DateTime.fromISO(state.summarydashboard.fromDate).toString(),
        to_date: DateTime.fromISO(state.summarydashboard.toDate).plus({ days: 1 }).toString(),
        compliance_report: state.summarydashboard.complianceReport,
        enable_date_adjustment: "true",
    });
    const response = await auth.fetch(`/api/get-facility-cycles?${facilityCycleParams}`);
    return response;
});

export const fetchCustomerCycles = createAsyncThunk("summarydashboard/fetchCustomerCycles", async (arg, { getState }) => {
    const state = getState();
    const customerCycleParams = new URLSearchParams({
        customer_id: state.summarydashboard.customer.id,
        // Add/Subtract 1 day so that the date range is inclusive
        from_date: DateTime.fromISO(state.summarydashboard.fromDate).toString(),
        to_date: DateTime.fromISO(state.summarydashboard.toDate).plus({ days: 1 }).toString(),
        compliance_report: state.summarydashboard.complianceReport,
        enable_date_adjustment: "true",
    });
    const response = await auth.fetch(`/api/get-customer-cycles?${customerCycleParams}`);
    return response;
});

export const fetchCycleEvents = createAsyncThunk(
    "summarydashboard/fetchCycleEvents",
    /** @param {string} cycleId  */
    async (cycleId, { getState }) => {
        const state = getState();
        const params = new URLSearchParams({
            device_config_id: state.summarydashboard.selectedCycle.device_config_id,
            from_date: DateTime.fromISO(state.summarydashboard.fromDate).minus({ days: 1 }).toString(),
            to_date: DateTime.fromISO(state.summarydashboard.toDate).plus({ days: 1 }).toString(),
            compliance_report: state.summarydashboard.complianceReport,
        });

        if (state.summarydashboard.selectedCycle) {
            params.append("cycle_id", state.summarydashboard.selectedCycle.cycle_id);
        } else if (cycleId) {
            params.append("cycle_id", cycleId);
        }

        const response = await auth.fetch(`/api/get-cycle-events?${params}`);
        return response;
    }
);

export const fetchCustomers = createAsyncThunk("summarydashboard/fetchCustomers", async (userGroup) => {
    const response = await auth.fetch(`/api/customer/`);
    return response;
});

export const selectFacilitiesDropdown = (state) => {
    return state.facilities.map((facility) => {
        return { id: facility.id, value: facility.id, name: facility.facility_name };
    });
};

export const selectCyclesByFacilities = (state, facilityNames) => {
    if (!facilityNames || facilityNames.length === 0) {
        return state.cycles;
    }
    return state.cycles.filter((cycle) => facilityNames.includes(cycle.facility_name));
};

export const selectFacilitiesDropdownByCustomer = (state, customerId) => {
    return customerId
        ? state.facilities
            .filter((facility) => facility?.customer?.id === customerId)
            .map((facility) => {
                return { id: facility.id, value: facility.id, name: facility.facility_name };
            })
        : [];
};

export const selectFilteredDeviceConfigsByFacilities = (state, facilityIds) => {
    if (!facilityIds || facilityIds.length === 0) {
        return state.deviceConfigIds;
    }
    return state.facilities.filter((facility) => facilityIds.includes(facility.id)).map((facility) => facility.device_configs.map((deviceConfig) => deviceConfig.id)).flat();
};

export const selectFilteredBiochallengeInfoByFacilities = (state, facilityIds) => {
    if (!facilityIds || facilityIds.length === 0) {
        return state.biochallengeInfo;
    }
    return state.biochallengeInfo.filter((info) => facilityIds.includes(info.facility_id));
}


export const fetchBiochallengeInfo = createAsyncThunk(
    "summarydashboard/fetchBiochallengeInfo",
    async (customerId, { getState }) => {
        const state = getState();
        const response = await auth.fetch(`/api/get-summary-biochallenge-info?customer_id=${customerId}`);

        return response;
    }
);

export const {
    setUserTimezone,
    setToDate,
    setFromDate,
    setSelectedCycle,
    setCycleMetadata,
    setCycleEvents,
    setCycles,
    setDeviceConfigIds,
    setDeviceSerial,
    setComplianceReport,
    setSelectedDevice,
    setComprehensiveReport,
    setIsError,
    setCurrentPressureUnit,
    setDeviceFacility,
    setDeviceFacilityAddress,
    setDeviceTimezone,
    setRawCustomerDevices,
    setCustomerDevices,
    setFilteredCustomerDevices,
    setBiochallengeInfo,
    setCustomerDevicesOnly,
    setWeightUnit,
    setRefreshInterval,
    setNoCyclesFound,
    setSelectedCustomer,
    setSelectedFacilities,
} = summaryDashboardSlice.actions;

export default summaryDashboardSlice.reducer;
